/* eslint-disable no-useless-escape */
import React from "react";
import propTypes from "prop-types";
import classnames from "classnames";
import Label from "./Label";
import ErrorsForField from "../form/ErrorsForField";
import { useField } from "../form/Form";
import Button from "../button/Button";

import "./Input.scss";
import { createDataAttributes } from "../../utilities";

const emailRegex =
  /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const InputEmail = ({
  id,
  name,
  label,
  labelElement,
  placeholder,
  value,
  maxLength,
  className,
  disabled,
  onChange,
  onBlur,
  onKeyDown,
  onValidate,
  required,
  isClearable,
  size,
  hideLabel,
  noLabel,
  fieldErrors,
  hideErrors,
  requiredError,
  dataAttrib,
  dataWrapperAttrib,
  ...rest
}) => {
  const sizeClass = size ? "uikit__input-size__" + size : null;

  const containerClasses = classnames(
    "uikit__form-item",
    "uikit__input-wrapper",
    className,
    sizeClass,
    { "uikit__hide-label": hideLabel, "uikit__form__hide-errors": hideErrors }
  );
  const inputClass = classnames("uikit__input-email", {
    "has-error": !!fieldErrors?.length,
    "is-required": required,
    "has-value": !!value
  });

  const handleChange = event => {
    onChange({ event, field: name, value: event.currentTarget.value });
  };

  const handleBlur = event => {
    const v = event?.currentTarget?.value || "";

    // if the value has white space at beginning or end of value,
    // ...trim it and update the input
    if (/^\s|\s$/.test(v)) {
      onChange({ event, field: name, value: v.trim() });
    }

    onBlur({ event, field: name, value: v.trim() });
  };

  const handleKeyDown = event => {
    if (onKeyDown) {
      onKeyDown({ event, value: event.currentTarget.value });
    }
  };

  /*
  handle custom component validation

  validation to allow:
  - valid email address
  */
  const handleOnValidate = ({ field, value, validation }) => {
    const errors = onValidate({ field, value, validation });
    if (!!value && !emailRegex.test(value)) {
      errors.push("Not a valid email address.");
    }
    return errors;
  };

  useField({
    name,
    validation: { required, requiredError },
    onValidate: handleOnValidate,
    label
  });

  const clearField = event => {
    onChange({ event, field: name, value: "" });
  };

  return (
    <div
      className={containerClasses}
      {...createDataAttributes(dataWrapperAttrib)}
    >
      {!noLabel && (
        <Label
          htmlFor={id}
          label={label}
          labelElement={labelElement}
          required={required}
        />
      )}
      <div
        className={classnames("uikit__input-input", {
          "uikit__input-clearable": isClearable
        })}
      >
        <input
          type="text"
          id={id}
          name={name}
          value={value}
          className={inputClass}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          maxLength={maxLength}
          placeholder={placeholder}
          disabled={disabled}
          aria-describedby={`errors_for_${id}`}
          {...createDataAttributes(dataAttrib)}
          {...rest}
        />
        {isClearable && (
          <Button
            icon="fasTimes"
            wrapperClassName="uikit__input-button"
            tabIndex={-1}
            onClick={clearField}
          />
        )}
      </div>
      {!hideErrors && !!fieldErrors?.length && (
        <ErrorsForField id={`errors_for_${id}`} errors={fieldErrors} />
      )}
    </div>
  );
};

InputEmail.propTypes = {
  //global form props
  className: propTypes.string,
  disabled: propTypes.bool,

  id: propTypes.string.isRequired,
  label: propTypes.oneOfType([
    propTypes.string,
    propTypes.object,
    propTypes.element,
    propTypes.func
  ]),
  labelElement: propTypes.element,
  name: propTypes.string.isRequired,
  onBlur: propTypes.func.isRequired,
  onChange: propTypes.func.isRequired,
  onKeyDown: propTypes.func,
  onValidate: propTypes.func.isRequired,
  required: propTypes.bool,
  value: propTypes.string.isRequired,
  hideLabel: propTypes.bool,
  noLabel: propTypes.bool,
  requiredError: propTypes.string,
  isClearable: propTypes.bool,
  fieldErrors: propTypes.array,
  hideErrors: propTypes.bool,

  //component-specific
  maxLength: propTypes.number,
  placeholder: propTypes.string,
  size: propTypes.oneOf([
    "auto",
    "full",
    "tiny",
    "sm",
    "md",
    "lg",
    "25p",
    "33p",
    "50p",
    "fill"
  ]),
  componentname: propTypes.string,
  dataAttrib: propTypes.arrayOf(Object),
  dataWrapperAttrib: propTypes.arrayOf(Object)
};

InputEmail.defaultProps = {
  maxLength: 250,
  required: false,
  placeholder: "email@address.com",
  size: "md",
  hideErrors: false,
  componentname: "InputEmail"
};

export default InputEmail;
